import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrackingInput from './components/TrackingInput';
import TrackingResults from './components/TrackingResults';
import { supabase, type Shipment } from '../../lib/supabase';

const TrackingPage = () => {
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [searchParams] = useSearchParams();
  const autoTracked = useRef(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://track-verse.com';

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://track-verse.com';
    document.title = 'Suivi Colis en Temps Réel — Tracking Envoi International | TrackVerse';
    document.documentElement.lang = 'fr';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Suivez votre colis en temps réel avec TrackVerse. Entrez votre numéro de suivi pour obtenir des mises à jour instantanées sur la localisation, le statut et la date de livraison estimée — dans 50+ pays.');
    setMeta('keywords', 'suivi colis, tracking envoi, numéro de suivi, localisation colis, statut livraison, TrackVerse tracking');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); (canonical as HTMLLinkElement).rel = 'canonical'; document.head.appendChild(canonical); }
    (canonical as HTMLLinkElement).href = `${siteUrl}/tracking`;

    setMeta('og:title', 'Suivi Colis Temps Réel — TrackVerse Tracking', true);
    setMeta('og:description', 'Suivez votre envoi en temps réel dans 50+ pays. Entrez votre numéro de suivi pour un accès instantané.', true);
    setMeta('og:url', `${siteUrl}/tracking`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:image', `${siteUrl}/og-image.jpg`, true);
    setMeta('twitter:title', 'Suivi Colis Temps Réel — TrackVerse');
    setMeta('twitter:description', 'Suivez votre envoi en temps réel dans 50+ pays. Tracking instantané.');
    setMeta('twitter:image', `${siteUrl}/og-image.jpg`);

    const scriptId = 'tracking-page-schema';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Suivi Colis en Temps Réel",
      "url": `${siteUrl}/tracking`,
      "description": "Suivez votre colis en temps réel avec TrackVerse dans 50+ pays.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
          { "@type": "ListItem", "position": 2, "name": "Suivi Colis", "item": `${siteUrl}/tracking` }
        ]
      },
      "mainEntity": {
        "@type": "WebApplication",
        "name": "TrackVerse Package Tracking",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Any",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" }
      }
    };

    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(webPageSchema);

    return () => { const s = document.getElementById(scriptId); if (s) s.remove(); };
  }, []);

  const handleTrack = async (trackingNumber: string) => {
    setLoading(true);
    setError(null);
    setSearched(true);
    setShipment(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('shipments')
        .select('*')
        .eq('tracking_number', trackingNumber)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!data) {
        setError('No shipment found with this tracking number.');
      } else {
        setShipment(data);
      }
    } catch (err) {
      console.error('Error fetching shipment:', err);
      setError('An error occurred while fetching shipment information.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-track si ?track= est dans l'URL (hero ou QR code) — une seule fois
  useEffect(() => {
    if (autoTracked.current) return;
    const trackParam = searchParams.get('track');
    if (trackParam && trackParam.trim()) {
      autoTracked.current = true;
      handleTrack(trackParam.trim());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/30 overflow-x-hidden">
      <Navbar />
      <div className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-gray-900 mb-3 sm:mb-5">
              Suivre mon colis
            </h1>
            <p className="text-sm sm:text-lg text-gray-500">
              Entrez votre numéro de suivi pour des mises à jour en temps réel
            </p>
          </div>

          <TrackingInput onTrack={handleTrack} />

          {searched && (
            <TrackingResults
              shipment={shipment}
              loading={loading}
              error={error}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TrackingPage;
